/**
 * stepLabels — plain-language labels for generation step log entries.
 *
 * The backend step log (backend/template_content_crew/agent_sdk_writer/
 * step_log.py → humanize_trace_event / _tool_use_label) emits messages aimed
 * at developers ("Using verify_section_output", "Fetching https://…"). This
 * pure module maps every known message pattern to language the SEO review
 * team reads without knowing tool names.
 *
 * Rules:
 * - Unknown messages fall back to the raw msg unchanged (graceful).
 * - The raw tool name is NEVER part of the label — callers surface it in a
 *   title tooltip for debugging only.
 * - Suffixed variants ("… — result", "… (failed)") humanize their base label
 *   and re-attach a plain suffix.
 */

/** Known bare tool names (last `__` segment, lowercased) → reviewer label. */
const TOOL_LABELS: Record<string, string> = {
  verify_section_output: 'Self-check: section quality',
  verify_sources_alive: 'Self-check: sources still live',
  verify_source_alive: 'Self-check: sources still live',
  structuredoutput: 'Submitted section draft',
  get_brand_context: 'Loaded brand voice',
  lookup_section_sources: 'Looked up approved sources',
  lookup_internal_link_candidates: 'Looked up internal links',
};

/** "https://www.example.com/path" → "example.com"; non-URLs pass through. */
function sourceDomain(target: string): string {
  const trimmed = target.trim();
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const host = new URL(withScheme).hostname.replace(/^www\./, '');
    if (host && host.includes('.')) return host;
  } catch {
    // Not a URL — keep the original text.
  }
  return trimmed;
}

const RESULT_SUFFIX = ' — result';
const FAILED_SUFFIX = ' (failed)';

/**
 * Map a step-log `msg` to a label a non-developer reviewer understands.
 * Falls back to the original msg when the pattern is unknown.
 */
export function humanizeStepLabel(msg: string): string {
  const raw = (msg ?? '').trim();
  if (!raw) return raw;

  // Suffixed variants recurse on their base label.
  if (raw.endsWith(RESULT_SUFFIX)) {
    const base = humanizeStepLabel(raw.slice(0, -RESULT_SUFFIX.length));
    return `${base} — result`;
  }
  if (raw.endsWith(FAILED_SUFFIX)) {
    const base = humanizeStepLabel(raw.slice(0, -FAILED_SUFFIX.length));
    return `${base} — failed`;
  }

  // Exact messages
  if (raw === 'Tool call failed') return 'Step failed';
  if (raw === 'Insights') return 'Reasoning';
  if (raw === 'Searching the web') return 'Web search';
  if (raw === 'Fetching source') return 'Read source';
  if (raw === 'Checking approved source pool') return 'Looked up approved sources';
  if (raw === 'Checking internal link candidates') return 'Looked up internal links';
  if (raw === 'Drafting section content') return 'Drafted section';
  if (raw === 'Revising section') return 'Revised section';

  // Parameterized messages
  let match = raw.match(/^Searching for (.+)$/s);
  if (match) return `Web search: ${match[1]}`;

  match = raw.match(/^Fetching (.+)$/s);
  if (match) return `Read source: ${sourceDomain(match[1])}`;

  match = raw.match(/^Checking approved source pool: (.+)$/s);
  if (match) return `Looked up approved sources — ${match[1]}`;

  match = raw.match(/^Checking internal link candidates: (.+)$/s);
  if (match) return `Looked up internal links — ${match[1]}`;

  match = raw.match(/^Drafting "(.+)"$/s);
  if (match) return `Drafted: ${match[1]}`;

  match = raw.match(/^Revising "(.+)"$/s);
  if (match) return `Revised: ${match[1]}`;

  // "Using <tool>" — known tools get a purpose label; unknown ones get a
  // readable "Ran: …" with underscores/namespaces stripped.
  match = raw.match(/^Using (.+)$/s);
  if (match) {
    const bare = match[1].split('__').pop() ?? match[1];
    const known = TOOL_LABELS[bare.toLowerCase()];
    if (known) return known;
    return `Ran: ${bare.replace(/_/g, ' ')}`;
  }

  return raw;
}
